import React, { useState, useRef } from 'react';
import {
  Plus, Search, Edit, Trash2, Eye, EyeOff,
  Calendar, Image as ImageIcon, Upload, X,
  CheckCircle, AlertCircle, ToggleLeft, ToggleRight,
  Grid, List, Link as LinkIcon,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import {
  useGetBannersQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useToggleBannerStatusMutation,
  useDeleteBannerMutation,
} from '../app/api';

const Banners = () => {
  const fileInputRef = useRef(null);

  // ============================================
  // STATE
  // ============================================
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedBanner, setSelectedBanner] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: null,
    imagePreview: null,
    existingImage: null,
    thumbnail: null,
    thumbnailPreview: null,
    link: '',
    position: 'home_top',
    status: 'Active',
    startDate: '',
    endDate: '',
  });
  const [errors, setErrors] = useState({});

  // ============================================
  // ✅ RTK QUERY — Auto cache
  // ============================================
  const { data, isLoading } = useGetBannersQuery({
    page,
    limit,
    search: searchTerm || undefined,
    status: filterStatus !== 'All' ? filterStatus : undefined,
  });

  const [createBanner, { isLoading: creating }] = useCreateBannerMutation();
  const [updateBanner, { isLoading: updating }] = useUpdateBannerMutation();
  const [toggleBannerStatus] = useToggleBannerStatusMutation();
  const [deleteBanner] = useDeleteBannerMutation();

  // ============================================
  // DATA
  // ============================================
  const banners = data?.data?.banners || [];
  const imageBaseUrl = data?.data?.imageBaseUrl || '';
  const pagination = data?.data?.pagination || {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  // ============================================
  // HELPERS
  // ============================================
  const getFullImage = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${imageBaseUrl}${imagePath}`;
  };

  const positionOptions = [
    { value: 'home_top', label: 'Home Page - Top' },
    { value: 'home_middle', label: 'Home Page - Middle' },
    { value: 'home_bottom', label: 'Home Page - Bottom' },
    { value: 'sidebar', label: 'Sidebar' },
    { value: 'category_top', label: 'Category Page - Top' },
    { value: 'product_page', label: 'Product Page' },
  ];

  const statusOptions = ['All', 'Active', 'Inactive'];

  const getStatusBadge = (status) => {
    const classes = {
      Active: 'badge-success',
      Inactive: 'badge-danger',
    };
    return classes[status] || 'badge-info';
  };

  const getStatusIcon = (status) => {
    const icons = {
      Active: <CheckCircle size={14} />,
      Inactive: <EyeOff size={14} />,
    };
    return icons[status] || <AlertCircle size={14} />;
  };

  const getPositionLabel = (position) => {
    const labels = {
      home_top: 'Home Top',
      home_middle: 'Home Middle',
      home_bottom: 'Home Bottom',
      sidebar: 'Sidebar',
      category_top: 'Category Top',
      product_page: 'Product Page',
    };
    return labels[position] || position;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // ============================================
  // FORM HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, WEBP allowed');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB allowed');
      return false;
    }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        image: file,
        imagePreview: reader.result,
        existingImage: null,
      }));
      setErrors((prev) => ({ ...prev, image: '' }));
    };
    reader.readAsDataURL(file);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        thumbnail: file,
        thumbnailPreview: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
      existingImage: null,
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.image && !formData.existingImage) {
      newErrors.image = 'Image is required';
    }
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }
    return newErrors;
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      image: null,
      imagePreview: null,
      existingImage: null,
      thumbnail: null,
      thumbnailPreview: null,
      link: '',
      position: 'home_top',
      status: 'Active',
      startDate: '',
      endDate: '',
    });
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ============================================
  // CREATE
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors');
      return;
    }

    try {
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('subtitle', formData.subtitle || '');
      fd.append('link', formData.link || '');
      fd.append('position', formData.position);
      fd.append('status', formData.status);
      if (formData.startDate) fd.append('startDate', formData.startDate);
      if (formData.endDate) fd.append('endDate', formData.endDate);
      if (formData.image) fd.append('image', formData.image);
      if (formData.thumbnail) fd.append('thumbnail', formData.thumbnail);

      await createBanner(fd).unwrap();
      toast.success('Banner created successfully!');
      setShowAddModal(false);
      resetForm();
      // ✅ Auto refetch via invalidatesTags
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to create banner');
    }
  };

  // ============================================
  // EDIT
  // ============================================
  const handleEditClick = (banner) => {
    setSelectedBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      image: null,
      imagePreview: null,
      existingImage: banner.image || null,
      thumbnail: null,
      thumbnailPreview: null,
      link: banner.link || '',
      position: banner.position || 'home_top',
      status: banner.status || 'Active',
      startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
      endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors');
      return;
    }

    try {
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('subtitle', formData.subtitle || '');
      fd.append('link', formData.link || '');
      fd.append('position', formData.position);
      fd.append('status', formData.status);
      if (formData.startDate) fd.append('startDate', formData.startDate);
      if (formData.endDate) fd.append('endDate', formData.endDate);
      if (formData.image) fd.append('image', formData.image);
      if (formData.thumbnail) fd.append('thumbnail', formData.thumbnail);

      await updateBanner({ id: selectedBanner._id, formData: fd }).unwrap();
      toast.success('Banner updated successfully!');
      setShowEditModal(false);
      resetForm();
      // ✅ Auto refetch
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update banner');
    }
  };

  // ============================================
  // TOGGLE STATUS
  // ============================================
  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

    if (!window.confirm(`Change status to ${newStatus}?`)) return;

    setTogglingId(id);

    try {
      await toggleBannerStatus(id).unwrap();
      toast.success(`Status: ${newStatus}`);
      // ✅ Auto refetch
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to toggle status');
    } finally {
      setTogglingId(null);
    }
  };

  // ============================================
  // DELETE
  // ============================================
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      await deleteBanner(id).unwrap();
      toast.success('Banner deleted successfully');

      if (banners.length === 1 && page > 1) {
        setPage(page - 1);
      }
      // Auto refetch
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete banner');
    }
  };

  // ============================================
  // VIEW
  // ============================================
  const handleView = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  // ============================================
  // PAGINATION
  // ============================================
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const stats = {
    total: pagination.total,
    active: banners.filter((b) => b.status === 'Active').length,
    inactive: banners.filter((b) => b.status === 'Inactive').length,
  };

  const getPageNumbers = () => {
    const { page: curPage, totalPages } = pagination;
    const pages = [];
    const maxVisible = 5;

    let start = Math.max(1, curPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <Layout>
      <div className="banners-page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Banners</h1>
            <p className="page-subtitle">Manage your promotional banners</p>
          </div>
          <div className="page-actions">
            <button
              className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </button>
            <button
              className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              <Plus size={18} />
              Add Banner
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="banners-stats">
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-primary)' }}>
              <ImageIcon size={20} />
            </div>
            <div>
              <span className="stat-label">Total</span>
              <span className="stat-value">{stats.total}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-success)' }}>
              <CheckCircle size={20} />
            </div>
            <div>
              <span className="stat-label">Active</span>
              <span className="stat-value">{stats.active}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-danger)' }}>
              <EyeOff size={20} />
            </div>
            <div>
              <span className="stat-label">Inactive</span>
              <span className="stat-value">{stats.inactive}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="banners-filters card">
          <div className="filter-row">
            <div className="filter-group">
              <div className="search-box">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search banners..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="search-input"
                />
                {searchTerm && (
                  <button className="clear-search" onClick={() => setSearchTerm('')}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="filter-group">
              <select
                className="filter-select"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status === 'All' ? 'All Status' : status}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <select
                className="filter-select"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex-center" style={{ height: '300px' }}>
            <div className="spinner" />
          </div>
        ) : banners.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-content">
                <span className="empty-icon">🎯</span>
                <h3>No banners found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="banners-grid">
                {banners.map((banner) => (
                  <div key={banner._id} className="banner-card">
                    <div className="banner-image">
                      {banner.image ? (
                        <img src={getFullImage(banner.image)} alt={banner.title} />
                      ) : (
                        <div className="image-placeholder">
                          <ImageIcon size={40} />
                        </div>
                      )}
                      <div className="banner-overlay">
                        <span className="banner-position">
                          {getPositionLabel(banner.position)}
                        </span>
                      </div>
                    </div>

                    <div className="banner-content">
                      <div className="banner-header">
                        <div>
                          <h4 className="banner-title">{banner.title}</h4>
                          {banner.subtitle && (
                            <p className="banner-subtitle">{banner.subtitle}</p>
                          )}
                        </div>
                        <button
                          className={`status-toggle ${banner.status === 'Active' ? 'active' : ''}`}
                          onClick={() => handleToggleStatus(banner._id, banner.status)}
                          disabled={togglingId === banner._id}
                        >
                          {togglingId === banner._id ? (
                            <span className="spinner-sm" />
                          ) : banner.status === 'Active' ? (
                            <ToggleRight size={24} />
                          ) : (
                            <ToggleLeft size={24} />
                          )}
                        </button>
                      </div>

                      <div className="banner-meta">
                        {banner.link && (
                          <div className="meta-item">
                            <LinkIcon size={14} />
                            <span className="truncate">{banner.link}</span>
                          </div>
                        )}
                        {(banner.startDate || banner.endDate) && (
                          <div className="meta-item">
                            <Calendar size={14} />
                            <span>
                              {formatDate(banner.startDate)} - {formatDate(banner.endDate)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="banner-actions">
                        <span className={`badge ${getStatusBadge(banner.status)}`}>
                          {getStatusIcon(banner.status)}
                          {banner.status}
                        </span>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => handleView(banner)}>
                            <Eye size={16} />
                          </button>
                          <button className="btn btn-sm btn-primary" onClick={() => handleEditClick(banner)}>
                            <Edit size={16} />
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(banner._id)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card banners-table-container">
                <div className="table-container">
                  <table className="table banners-table">
                    <thead>
                      <tr>
                        <th>Banner</th>
                        <th>Position</th>
                        <th>Date Range</th>
                        <th>Link</th>
                        <th>Status</th>
                        <th style={{ width: '130px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {banners.map((banner) => (
                        <tr key={banner._id} className="banner-row">
                          <td>
                            <div className="banner-info">
                              <div className="banner-thumb">
                                {banner.image ? (
                                  <img src={getFullImage(banner.image)} alt={banner.title} />
                                ) : (
                                  <ImageIcon size={20} />
                                )}
                              </div>
                              <div>
                                <div className="banner-title-text">{banner.title}</div>
                                {banner.subtitle && (
                                  <div className="banner-subtitle-text">{banner.subtitle}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="banner-position-badge">
                              {getPositionLabel(banner.position)}
                            </span>
                          </td>
                          <td>
                            <div className="date-range">
                              {banner.startDate || banner.endDate ? (
                                <>
                                  <span>{formatDate(banner.startDate)}</span>
                                  <span className="date-arrow">→</span>
                                  <span>{formatDate(banner.endDate)}</span>
                                </>
                              ) : (
                                <span className="text-muted">No dates</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {banner.link ? (
                              <span className="truncate" title={banner.link}>
                                {banner.link}
                              </span>
                            ) : (
                              <span className="text-muted">No link</span>
                            )}
                          </td>
                          <td>
                            <button
                              className={`badge ${getStatusBadge(banner.status)} status-toggle-btn`}
                              onClick={() => handleToggleStatus(banner._id, banner.status)}
                              disabled={togglingId === banner._id}
                            >
                              {togglingId === banner._id ? (
                                <span className="spinner-sm" />
                              ) : (
                                getStatusIcon(banner.status)
                              )}
                              {banner.status}
                            </button>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn btn-sm btn-outline" onClick={() => handleView(banner)}>
                                <Eye size={16} />
                              </button>
                              <button className="btn btn-sm btn-primary" onClick={() => handleEditClick(banner)}>
                                <Edit size={16} />
                              </button>
                              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(banner._id)}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pagination.total > 0 && (
                  <div className="table-footer">
                    <div className="table-info">
                      Showing <strong>{(pagination.page - 1) * pagination.limit + 1}</strong> to{' '}
                      <strong>{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> of{' '}
                      <strong>{pagination.total}</strong> banners
                    </div>
                    <div className="pagination">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrevPage}
                      >
                        <ChevronLeft size={16} />
                        Prev
                      </button>
                      {getPageNumbers().map((pageNum, index) =>
                        pageNum === '...' ? (
                          <span key={`dots-${index}`} className="pagination-dots">
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            className={`btn btn-sm ${
                              pagination.page === pageNum ? 'btn-primary' : 'btn-outline'
                            }`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        )
                      )}
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNextPage}
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Banner</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label className="form-label">Title <span className="required">*</span></label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`form-control ${errors.title ? 'error' : ''}`}
                />
                {errors.title && <div className="form-error">{errors.title}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Image <span className="required">*</span></label>
                <div className="image-upload-container">
                  {formData.imagePreview ? (
                    <div className="image-preview-wrapper">
                      <img src={formData.imagePreview} alt="Preview" className="image-preview" />
                      <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="image-upload-area">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        id="banner-image"
                      />
                      <label htmlFor="banner-image" style={{ cursor: 'pointer' }}>
                        <Upload size={32} className="upload-icon" />
                        <p className="upload-text">Click to upload</p>
                        <span className="upload-hint">PNG, JPG, WEBP (Max 5MB)</span>
                      </label>
                    </div>
                  )}
                </div>
                {errors.image && <div className="form-error">{errors.image}</div>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Link</label>
                  <input
                    type="text"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="form-control"
                  >
                    {positionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`form-control ${errors.endDate ? 'error' : ''}`}
                  />
                  {errors.endDate && <div className="form-error">{errors.endDate}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Saving...' : 'Create Banner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && selectedBanner && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Banner</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>

            <form onSubmit={handleEdit} className="modal-body">
              <div className="form-group">
                <label className="form-label">Title <span className="required">*</span></label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`form-control ${errors.title ? 'error' : ''}`}
                />
                {errors.title && <div className="form-error">{errors.title}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Image</label>
                <div className="image-upload-container">
                  {formData.imagePreview || formData.existingImage ? (
                    <div className="image-preview-wrapper">
                      <img
                        src={formData.imagePreview || getFullImage(formData.existingImage)}
                        alt="Preview"
                        className="image-preview"
                      />
                      <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>
                        <X size={18} />
                      </button>
                      <div className="image-badge">
                        {formData.image ? 'New' : 'Existing'}
                      </div>
                    </div>
                  ) : (
                    <div className="image-upload-area">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        id="banner-image-edit"
                      />
                      <label htmlFor="banner-image-edit" style={{ cursor: 'pointer' }}>
                        <Upload size={32} className="upload-icon" />
                        <p className="upload-text">Click to upload</p>
                      </label>
                    </div>
                  )}
                </div>
                {errors.image && <div className="form-error">{errors.image}</div>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Link</label>
                  <input
                    type="text"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="form-control"
                  >
                    {positionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`form-control ${errors.endDate ? 'error' : ''}`}
                  />
                  {errors.endDate && <div className="form-error">{errors.endDate}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={updating}>
                  {updating ? 'Saving...' : 'Update Banner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {showViewModal && selectedBanner && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{selectedBanner.title}</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              {selectedBanner.image && (
                <div className="view-banner-image">
                  <img src={getFullImage(selectedBanner.image)} alt={selectedBanner.title} />
                </div>
              )}

              <div className="details-grid">
                {selectedBanner.subtitle && (
                  <div className="detail-pair full-width">
                    <span className="detail-label">Subtitle</span>
                    <span className="detail-value">{selectedBanner.subtitle}</span>
                  </div>
                )}
                <div className="detail-pair">
                  <span className="detail-label">Position</span>
                  <span className="detail-value">{getPositionLabel(selectedBanner.position)}</span>
                </div>
                <div className="detail-pair">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">
                    <span className={`badge ${getStatusBadge(selectedBanner.status)}`}>
                      {selectedBanner.status}
                    </span>
                  </span>
                </div>
                {selectedBanner.link && (
                  <div className="detail-pair full-width">
                    <span className="detail-label">Link</span>
                    <span className="detail-value">{selectedBanner.link}</span>
                  </div>
                )}
                <div className="detail-pair">
                  <span className="detail-label">Start Date</span>
                  <span className="detail-value">{formatDate(selectedBanner.startDate)}</span>
                </div>
                <div className="detail-pair">
                  <span className="detail-label">End Date</span>
                  <span className="detail-value">{formatDate(selectedBanner.endDate)}</span>
                </div>
                <div className="detail-pair">
                  <span className="detail-label">Created</span>
                  <span className="detail-value">{formatDate(selectedBanner.createdAt)}</span>
                </div>
                <div className="detail-pair">
                  <span className="detail-label">Last Updated</span>
                  <span className="detail-value">{formatDate(selectedBanner.updatedAt)}</span>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowViewModal(false)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowViewModal(false);
                  handleEditClick(selectedBanner);
                }}
              >
                <Edit size={16} />
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Banners;